import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RecommendationDto } from '@shared/models';
import { TroubleshootingStore } from '@shared/state';
import { ButtonV3Component, ComplexDialogV3Service, IconV3Component } from '@gea/digital-ui-lib';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { RecommendationItemChipComponent } from './chip/recommendation-item-chip.component';
import { EditableRecommendationActionsComponent } from '@brc/tab-container/fault-recommendations/actions/actions.component';
import { ReadonlyActionsComponent } from '@brc/tab-container/troubleshooting/recommendations/actions/actions.component';
import { BRCBaseComponent } from '@brc/hrt-base-component';

@Component({
  selector: 'gea-hrt-recommendation-item',
  styleUrl: './recommendation-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonV3Component,
    TranslatePipe,
    TooltipModule,
    IconV3Component,
    EditableRecommendationActionsComponent,
    RecommendationItemChipComponent,
    ReadonlyActionsComponent,
  ],
  template: `
    <div class="recommendation-item container" [class.even]="index() % 2" role="group">
      @if (isDraggable()) {
        <geaui-icon-v3 file="scroll-vertical" class="images-button sort-icon" data-testid="btn-draggable" />
      }

      @if (isEditable()) {
        <span class="validation-count" data-testid="span-validation-count">
          @switch (recommendation().validationCount) {
            @case (1) {
              <geaui-icon-v3 file="done" />
            }
            @case (2) {
              <geaui-icon-v3 file="done_all" />
            }
          }
        </span>
      }

      <div class="text">
        <p class="action-name" data-testid="span-action-name">
          {{ recommendation().actionName }}
        </p>

        @if (hasImages()) {
          <span class="images" data-testid="icon-images" (click)="openImagesDialog(recommendation().images)">
            Images attached ({{ recommendation().images.length }})
          </span>
        }
      </div>

      @if (isEditable()) {
        @if (!isDraggable()) {
          <geaui-button-v3
            [disabled]="!recommendation().canValidate"
            type="secondary"
            data-testid="btn-validate"
            (click)="validateRecommendation(recommendation())">
            <span>
              {{ 'TROUBLESHOOTING.RECOMMENDATIONS.BUTTON_VALIDATE' | translate }}
            </span>
          </geaui-button-v3>
        }
      }

      <div class="usage-count">
        @if (recommendation().usageCount) {
          <span data-testid="span-usage-count"> ({{ recommendation().usageCount }}) </span>
        }
      </div>

      @for (chip of chips; track chip) {
        @if (recommendation()[chip]) {
          <gea-hrt-recommendation-item-chip [type]="chip" [isEditable]="isEditable()" />
        }
      }

      <div class="actions">
        @if (!isDraggable()) {
          @if (isEditable()) {
            <gea-hrt-recommendation-editable-actions [recommendation]="recommendation()" />
          }
          @if (!isEditable()) {
            <gea-hrt-readonly-actions [recommendation]="recommendation()" />
          }
        }
      </div>
    </div>
  `,
})
export class RecommendationItemComponent extends BRCBaseComponent {
  readonly recommendation = input.required<RecommendationDto>();
  readonly index = input.required<number>();
  readonly isEditable = input.required<boolean>();
  readonly isDraggable = input<boolean>();

  readonly chips = ['trainedUserOnly', 'warrantyInvalidatingRisk'] as const;

  private readonly dialogService = inject(ComplexDialogV3Service);
  private readonly troubleshootingStore = inject(TroubleshootingStore);

  readonly canValidate = computed(() => this.isEditable() && this.recommendation().validationCount < 2);

  readonly hasImages = computed(() => (this.recommendation().images?.length ?? 0) > 0);

  async openImagesDialog(images: (ArrayBuffer[] | string)[]) {
    if (!images?.length) return;

    const { RecommendationImagesDialogComponent } = await import('@shared/components');

    this.dialogService.open(
      {
        header: 'UI-LIB.ATTACHMENTS.ATTACHMENTS',
        data: { images },
        hideButtons: true,
        width: '80vw',
        height: '80vh',
        variant: 'custom',
      },
      RecommendationImagesDialogComponent
    );
  }

  validateRecommendation(recommendation: RecommendationDto) {
    if (!this.canValidate()) {
      return;
    }

    const updatedRecommendation = { ...recommendation, validationCount: 2 };

    this.troubleshootingStore.validateRecommendation(updatedRecommendation).subscribe({
      next: () => {
        this.showSuccess('VALIDATION_SUCCESSFUL');
      },
      error: () => {
        this.showError('VALIDATION_FAILED');
      },
    });
  }
}
